// @flow
import React from "react";
import styled, { css } from "styled-components";
import gql from "graphql-tag";
import { ExtendedQuery, type QueryRenderProps } from "@nested/utils";
import { debounce } from "lodash";
import MenuItems from "./MenuItems";
import StatusIcon from "./StatusIcon";

const POSTCODE_DETAILS = gql`
  query AddressPickerPostcodeDetails(
    $postcode: String!
    $includeBlacklisted: Boolean
  ) {
    postcodeDetails(
      postcode: $postcode
      includeBlacklisted: $includeBlacklisted
    ) {
      valid
      inServicedArea
      normalisedPostcode
      reasonInvalid
      latitude
      longitude
      addresses {
        id
        label
        postcode
        town
      }
    }
  }
`;

const Ul = styled.ul`
  border-radius: 4px;
  background-color: white;
  position: absolute;
  z-index: 10;
  top: calc(100% - 4px);
  left: 0;
  margin: 0px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  list-style-type: none;

  border-radius: 0px 0px 4px 4px;
  color: ${({ theme }) => theme.palette.navy.light};
  ${({ highContrast }) =>
    highContrast
      ? css`
          border: 1px solid #bbb;
          border-top: none;
        `
      : css`
          box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.05);
          border: 1px solid #f5f5f5;
          border-top: none;
        `};
`;

const Li = styled.li`
  padding: 10px;
`;

const FetchError = ({ highContrast }) => (
  <>
    <StatusIcon warning />
    <Ul highContrast={highContrast}>
      <Li data-test="address-fetch-error">
        Failed to load addresses. Please try again later
      </Li>
    </Ul>
  </>
);

const Placeholder = () => <StatusIcon loading />;

type Props = {
  postcode: string,
  includeBlacklisted?: boolean,
  onChange(
    value: AddressPickerPostcodeDetails_postcodeDetails_addresses, // eslint-disable-line
  ): void,
  debounceTimeout?: number,
  onPostcodeNotInServicedArea?: any,
  highContrast?: boolean,
};

type State = {
  postcode: string,
};

class Menu extends React.Component<Props, State> {
  state = {
    postcode: this.props.postcode, // eslint-disable-line
  };

  updateQueryPostcode = debounce((postcode) => {
    this.setState({
      postcode,
    });
  }, this.props.debounceTimeout || 500); // eslint-disable-line

  componentDidUpdate(_: Props, { postcode: prevPostcode }: State) {
    const { postcode: newPostcode } = this.props;
    if (prevPostcode !== newPostcode) {
      this.updateQueryPostcode(newPostcode);
    }
  }

  render() {
    const {
      postcode: inputValue,
      onChange,
      onPostcodeNotInServicedArea,
      includeBlacklisted = false,
      highContrast,
    } = this.props;
    const { postcode } = this.state;
    const typing = inputValue !== postcode;
    if (postcode.length < 4) {
      return null;
    }
    return (
      <ExtendedQuery
        placeholder={Placeholder}
        errorHandler={() => <FetchError highContrast={highContrast} />}
        query={POSTCODE_DETAILS}
        variables={{
          postcode,
          includeBlacklisted,
        }}
      >
        {({
          data: { postcodeDetails },
          refetching,
        }: QueryRenderProps<AddressPickerPostcodeDetails>) => {
          if (!postcodeDetails) {
            return null;
          }
          return (
            <>
              <StatusIcon
                loading={typing || refetching}
                warning={
                  !postcodeDetails.valid || !postcodeDetails.inServicedArea
                }
              />
              <Ul highContrast={highContrast}>
                <MenuItems
                  postcodeValid={postcodeDetails.valid}
                  postcodeInServicedArea={postcodeDetails.inServicedArea}
                  addresses={postcodeDetails.addresses}
                  onClick={onChange}
                  onPostcodeNotInServicedArea={onPostcodeNotInServicedArea}
                  postcode={postcode}
                  postcodeLat={postcodeDetails.latitude}
                  postcodeLon={postcodeDetails.longitude}
                />
              </Ul>
            </>
          );
        }}
      </ExtendedQuery>
    );
  }
}

export default Menu;
